import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { useNotification } from '../../contexts/NotificationContext';
import { useRoleForm } from './shared/hooks/useRoleForm';
import { useRole } from './shared/services/useRoleService';
import RoleForm from './shared/sections/RoleForm';

/**
 * Single page for both Add and Edit Role.
 * Route /addRole         → add mode
 * Route /editRole/:role_id → edit mode
 */
const RolePage = () => {
  const { role_id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isSaving, setIsSaving] = useState(false);

  const { isEdit, loading, existingData, submit } = useRole(role_id);
  const { formData, setInitialData, handleNameChange, handleDescriptionChange, togglePermission, handleQuickAction, validate, buildPayload } = useRoleForm();

  useEffect(() => { if (existingData) setInitialData(existingData); }, [existingData, setInitialData]);

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const res = await submit(buildPayload());
      const roleName = buildPayload().name;
      if (res.data.status === 'success' || res.status === 200) {
        showNotification('success', isEdit ? `Role "${roleName}" updated successfully!` : `Role "${roleName}" created successfully!`);
        setTimeout(() => navigate('/roleList'), 1000);
      } else {
        showNotification('error', res.data.message || 'An error occurred.');
      }
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Server error.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', color: '#94a3b8' }}>
        <Shield style={{ width: 48, height: 48, margin: '0 auto 16px', display: 'block', color: '#e2e8f0' }} />
        <p>Loading role data…</p>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/roleList')}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            background: '#f8fafc', color: '#64748b',
            border: '1px solid #e2e8f0', borderRadius: '10px',
            padding: '9px 14px', fontSize: '13px', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          ← Back to List
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
            {isEdit ? 'Edit Role' : 'Add New Role'}
          </h2>
          {isEdit && (
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#94a3b8' }}>
              Editing: <strong style={{ color: '#10b981' }}>{formData.name}</strong>
            </p>
          )}
        </div>
      </div>

      <RoleForm
        formData={formData}
        handleNameChange={handleNameChange}
        handleDescriptionChange={handleDescriptionChange}
        togglePermission={togglePermission}
        handleQuickAction={handleQuickAction}
        isSaving={isSaving}
        onSave={handleSave}
      />
    </div>
  );
};

export default RolePage;
